import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import AuthModal from '../components/AuthModal';
import { useAuth } from '../context/AuthContext';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';import { ChevronLeft, Download, ShoppingBag, Plus, Trash2, User, Phone, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { generateDevizPDF } from '../utils/pdfGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Calculator = () => {
  const { user, token } = useAuth();
  const [step, setStep] = useState(1); // 1 = main calculator, 2 = other calculators selection
  const [showOtherCalculators, setShowOtherCalculators] = useState(false);
  const [roofTypes, setRoofTypes] = useState([]);
  const [selectedRoofType, setSelectedRoofType] = useState(null);
  const [selectedSubType, setSelectedSubType] = useState('');
  const [phases, setPhases] = useState([]);
  const [selectedPhases, setSelectedPhases] = useState({});
  const [materials, setMaterials] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState({});
  const [phaseQuantities, setPhaseQuantities] = useState({});
  const [thicknessSelections, setThicknessSelections] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalMaterials, setTotalMaterials] = useState(0);
  const [totalLabor, setTotalLabor] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mainCalculatorReady, setMainCalculatorReady] = useState(false);
  
  // Shop products state for "ADAUGA MATERIALE DIN MAGAZIN"
  const [shopProducts, setShopProducts] = useState([]);
  const [showShopModal, setShowShopModal] = useState(false);
  const [selectedShopProducts, setSelectedShopProducts] = useState([]); // [{product, quantity}]
  
  // Services state
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  
  // Download dialog state
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [clientInfo, setClientInfo] = useState({
    clientName: '',
    clientPhone: '',
    projectName: ''
  });

  // Initialize main calculator directly with "Hidroizolații Bituminoase"
  useEffect(() => {
    initializeMainCalculator();
    fetchShopProducts();
    fetchServices();
  }, []);

  const initializeMainCalculator = async () => {
    try {
      const response = await axios.get(`${API}/roof-types`);
      setRoofTypes(response.data);
      
      // Find "Acoperiș tip terasă" or similar
      const flatRoof = response.data.find(rt => 
        rt.name.toLowerCase().includes('terasă') || 
        rt.name.toLowerCase().includes('plat')
      );
      
      if (flatRoof) {
        setSelectedRoofType(flatRoof);
        // Set "Hidroizolații Bituminoase" as default sub-type
        const bituminousSubType = flatRoof.sub_types?.find(st => 
          st.toLowerCase().includes('bituminoas')
        ) || flatRoof.sub_types?.[0];
        
        if (bituminousSubType) {
          setSelectedSubType(bituminousSubType);
          // Fetch phases and materials for this combination
          const phasesResponse = await axios.get(`${API}/roof-types/${flatRoof.id}/phases?sub_type=${bituminousSubType}`);
          setPhases(phasesResponse.data);
          
          const materialsResponse = await axios.get(`${API}/materials?roof_type_id=${flatRoof.id}`);
          setMaterials(materialsResponse.data);
          
          setMainCalculatorReady(true);
        }
      }
    } catch (error) {
      console.error('Error initializing calculator:', error);
      toast.error('Eroare la încărcarea calculatorului');
    }
  };

  useEffect(() => {
    if (selectedRoofType && selectedSubType && showOtherCalculators) {
      fetchPhases();
    }
  }, [selectedRoofType, selectedSubType]);

  useEffect(() => {
    if (selectedRoofType) {
      fetchMaterials();
    }
  }, [selectedRoofType]);

  useEffect(() => {
    calculateTotal();
  }, [selectedPhases, selectedMaterials, phaseQuantities, thicknessSelections, selectedShopProducts]);

  const fetchRoofTypes = async () => {
    try {
      const response = await axios.get(`${API}/roof-types`);
      setRoofTypes(response.data);
    } catch (error) {
      console.error('Error fetching roof types:', error);
      toast.error('Eroare la încărcarea tipurilor de acoperș');
    }
  };
  
  const fetchShopProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setShopProducts(response.data);
    } catch (error) {
      console.error('Error fetching shop products:', error);
    }
  };
  
  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchPhases = async () => {
    try {
      const response = await axios.get(`${API}/roof-types/${selectedRoofType.id}/phases?sub_type=${selectedSubType}`);
      setPhases(response.data);
      // Also fetch materials
      const materialsResponse = await axios.get(`${API}/materials?roof_type_id=${selectedRoofType.id}`);
      setMaterials(materialsResponse.data);
    } catch (error) {
      console.error('Error fetching phases:', error);
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/materials?roof_type_id=${selectedRoofType.id}`);
      setMaterials(response.data);
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  };

  const calculateTotal = () => {
    let totalMat = 0;
    let totalLab = 0;

    Object.keys(selectedPhases).forEach(phaseId => {
      if (selectedPhases[phaseId] && selectedMaterials[phaseId]) {
        const material = materials.find(m => m.id === selectedMaterials[phaseId]);
        const quantity = parseFloat(phaseQuantities[phaseId]) || 0;
        
        if (material && quantity > 0) {
          let materialPrice = material.price_per_unit;
          let laborPrice = material.labor_per_unit;
          
          // Check if this material has thickness options (termosistem)
          if (material.has_thickness_options && thicknessSelections[phaseId]) {
            const thickness = parseInt(thicknessSelections[phaseId]);
            const multiplier = thickness / 50; // Base is 50mm
            materialPrice = material.price_per_unit * multiplier;
            laborPrice = material.labor_per_unit * multiplier;
          }
          
          totalMat += materialPrice * quantity;
          totalLab += laborPrice * quantity;
        }
      }
    });

    // Add shop products total (prices are WITH TVA, so we use them directly for material cost, no labor)
    // Important: Shop prices include TVA, but for deviz we calculate without double-taxing
    selectedShopProducts.forEach(item => {
      // Price from shop is with TVA (19%), we use it as-is for the deviz total
      // since deviz will show prices without TVA and add TVA at the end
      const priceWithoutTVA = item.product.price / 1.19;
      totalMat += priceWithoutTVA * item.quantity;
    });

    setTotalMaterials(totalMat);
    setTotalLabor(totalLab);
    setTotalPrice(totalMat + totalLab);
  };

  // Shop product handlers
  const addShopProduct = (product) => {
    const existing = selectedShopProducts.find(item => item.product.id === product.id);
    if (existing) {
      setSelectedShopProducts(prev => 
        prev.map(item => 
          item.product.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setSelectedShopProducts(prev => [...prev, { product, quantity: 1 }]);
    }
    toast.success('Produs adăugat la deviz');
  };

  const updateShopProductQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeShopProduct(productId);
      return;
    }
    setSelectedShopProducts(prev =>
      prev.map(item =>
        item.product.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const removeShopProduct = (productId) => {
    setSelectedShopProducts(prev => prev.filter(item => item.product.id !== productId));
  };

  const handleRoofTypeSelect = (roofType) => {
    setSelectedRoofType(roofType);
    setSelectedService(null);
    if (roofType.sub_types && roofType.sub_types.length > 0) {
      setStep(2);
    } else {
      setStep(3);
    }
  };
  
  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setSelectedRoofType(null);
    // Convert service phases to calculator phases
    const servicePhases = service.phases.map((phase, index) => ({
      id: `service-phase-${service.id}-${index}`,
      name: phase.name,
      description: phase.description,
      order: phase.order
    }));
    setPhases(servicePhases);
    setStep(3);
  };

  const handleSubTypeSelect = (subType) => {
    setSelectedSubType(subType);
    setStep(3);
  };

  const handlePhaseToggle = (phaseId, checked) => {
    setSelectedPhases(prev => ({ ...prev, [phaseId]: checked }));
  };

  const handleMaterialSelect = (phaseId, materialId) => {
    setSelectedMaterials(prev => ({ ...prev, [phaseId]: materialId }));
  };

  const handleQuantityChange = (phaseId, quantity) => {
    setPhaseQuantities(prev => ({ ...prev, [phaseId]: quantity }));
  };

  // Open download dialog instead of directly downloading
  const openDownloadDialog = () => {
    // Check if there are items to download
    const hasItems = Object.keys(selectedPhases).some(phaseId => 
      selectedPhases[phaseId] && selectedMaterials[phaseId] && parseFloat(phaseQuantities[phaseId]) > 0
    ) || selectedShopProducts.length > 0;
    
    if (!hasItems) {
      toast.error('Selectați cel puțin o lucrare pentru a genera devizul');
      return;
    }
    
    setShowDownloadDialog(true);
  };

  const handleDownloadPDF = async () => {
    try {
      // Prepare items for PDF
      const pdfItems = [];
      
      Object.keys(selectedPhases).forEach(phaseId => {
        if (selectedPhases[phaseId] && selectedMaterials[phaseId]) {
          const phase = phases.find(p => p.id === phaseId);
          const material = materials.find(m => m.id === selectedMaterials[phaseId]);
          const quantity = parseFloat(phaseQuantities[phaseId]) || 0;
          
          if (phase && material && quantity > 0) {
            let matPrice = material.price_per_unit;
            let labPrice = material.labor_per_unit;
            let materialName = material.name;
            
            // Apply thickness multiplier if applicable
            if (material.has_thickness_options && thicknessSelections[phaseId]) {
              const thickness = parseInt(thicknessSelections[phaseId]);
              const multiplier = thickness / 50;
              matPrice = material.price_per_unit * multiplier;
              labPrice = material.labor_per_unit * multiplier;
              materialName = `${material.name} - ${thickness}mm`;
            }
            
            pdfItems.push({
              phaseName: phase.name,
              materialName: materialName,
              unit: material.unit,
              quantity: quantity,
              unitPrice: matPrice,
              unitLabor: labPrice,
              total: (matPrice + labPrice) * quantity
            });
          }
        }
      });
      
      // Add shop products to PDF (price without TVA since deviz adds TVA at end)
      selectedShopProducts.forEach(item => {
        const priceWithoutTVA = item.product.price / 1.19;
        pdfItems.push({
          phaseName: 'MATERIALE DIN MAGAZIN',
          materialName: item.product.name,
          unit: item.product.unit,
          quantity: item.quantity,
          unitPrice: priceWithoutTVA,
          unitLabor: 0, // No labor for shop products
          total: priceWithoutTVA * item.quantity,
          isShopProduct: true
        });
      });
      
      if (pdfItems.length === 0) {
        toast.error('Selectați cel puțin o lucrare pentru a genera devizul');
        return;
      }
      
      // Calculate totals
      let totalManopera = 0;
      let totalMaterial = 0;
      pdfItems.forEach(item => {
        totalManopera += item.unitLabor * item.quantity;
        totalMaterial += item.unitPrice * item.quantity;
      });
      const totalFaraTVA = totalManopera + totalMaterial;
      const tvaAmount = totalFaraTVA * 0.21;
      const totalCuTVA = totalFaraTVA + tvaAmount;
      
      // Save deviz to database and get number
      let devizNumber = '';
      try {
        const saveResponse = await axios.post(`${API}/devize`, {
          client_name: clientInfo.clientName || 'Client IzoMAG',
          client_phone: clientInfo.clientPhone || '',
          project_name: clientInfo.projectName || '',
          roof_type: selectedRoofType?.name || 'Acoperiș',
          sub_type: selectedSubType || '',
          items: pdfItems,
          total_manopera: totalManopera,
          total_material: totalMaterial,
          total_fara_tva: totalFaraTVA,
          tva: tvaAmount,
          total_cu_tva: totalCuTVA
        });
        devizNumber = saveResponse.data.deviz_number;
        toast.success(`Deviz ${devizNumber} salvat cu succes!`);
      } catch (err) {
        console.error('Error saving deviz:', err);
        // Continue with download even if save fails
      }
      
      const quoteData = {
        roofTypeName: selectedRoofType?.name || 'Acoperiș',
        subType: selectedSubType || '',
        items: pdfItems,
        totalPrice: totalPrice
      };
      
      const doc = generateDevizPDF(quoteData, {
        clientName: clientInfo.clientName,
        clientPhone: clientInfo.clientPhone,
        projectName: clientInfo.projectName,
        devizNumber: devizNumber
      });
      
      // Generate filename: DEV0001-dd.mm.yyyy-NumeClient
      const today = new Date();
      const dateStr = `${today.getDate().toString().padStart(2, '0')}.${(today.getMonth() + 1).toString().padStart(2, '0')}.${today.getFullYear()}`;
      const clientNameClean = (clientInfo.clientName || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
      const filename = devizNumber 
        ? `${devizNumber}-${dateStr}-${clientNameClean}.pdf`
        : `deviz-${dateStr}-${clientNameClean}.pdf`;
      
      doc.save(filename);
      toast.success('PDF descărcat cu succes!');
      
      // Close dialog and reset
      setShowDownloadDialog(false);
      setClientInfo({ clientName: '', clientPhone: '', projectName: '' });
      
      await saveQuote();
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Eroare la generarea PDF');
    }
  };

  const saveQuote = async () => {
    try {
      const items = [];
      Object.keys(selectedPhases).forEach(phaseId => {
        if (selectedPhases[phaseId] && selectedMaterials[phaseId]) {
          const phase = phases.find(p => p.id === phaseId);
          const material = materials.find(m => m.id === selectedMaterials[phaseId]);
          const quantity = parseFloat(phaseQuantities[phaseId]) || 0;
          if (phase && material && quantity > 0) {
            items.push({
              phase_id: phase.id,
              phase_name: phase.name,
              material_id: material.id,
              material_name: material.name,
              quantity: quantity,
              unit_price: material.price_per_unit,
              unit_labor: material.labor_per_unit,
              total: (material.price_per_unit + material.labor_per_unit) * quantity
            });
          }
        }
      });

      const quoteData = {
        roof_type_id: selectedRoofType.id,
        roof_type_name: selectedRoofType.name,
        sub_type: selectedSubType,
        items,
        total_materials: items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0),
        total_labor: items.reduce((sum, item) => sum + (item.unit_labor * item.quantity), 0),
        total_price: totalPrice,
        is_guest: !user
      };

      await axios.post(`${API}/quotes`, quoteData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Error saving quote:', error);
    }
  };

  const progress = showOtherCalculators ? ((step / 3) * 100) : 100;

  // Handler to switch to other calculators mode
  const openOtherCalculators = () => {
    setShowOtherCalculators(true);
    setStep(1);
    // Reset selections
    setSelectedPhases({});
    setSelectedMaterials({});
    setPhaseQuantities({});
    setSelectedShopProducts([]);
  };

  // Handler to go back to main calculator
  const backToMainCalculator = async () => {
    setShowOtherCalculators(false);
    setStep(1);
    // Reset and reload main calculator
    setSelectedPhases({});
    setSelectedMaterials({});
    setPhaseQuantities({});
    setSelectedShopProducts([]);
    await initializeMainCalculator();
  };

  // Loading state
  if (!mainCalculatorReady && !showOtherCalculators) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50" data-testid="calculator-page">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50" data-testid="calculator-page">
      <SEO title="Calculator Preturi Hidroizolatii" description="Calculator online preturi hidroizolatii. Calculeaza costul manoperei si materialelor pentru proiectul tau. Oferta personalizata instant." canonical="/calculator" />
      <Navbar />
      
      <div className="pt-20 sm:pt-24 pb-8 sm:pb-12 px-3 sm:px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-6 sm:mb-8"
          >
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-2 sm:mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="calculator-title">
              {showOtherCalculators ? 'Alte Tipuri de Calculator' : 'Calculator Hidroizolații Bituminoase'}
            </h1>
            <p className="text-sm sm:text-lg text-slate-600 mb-4">
              {showOtherCalculators 
                ? 'Selectează tipul de acoperiș și calculează costul' 
                : 'Calculează costul lucrărilor de hidroizolații'}
            </p>
            
            {/* Button to toggle between main and other calculators */}
            {!showOtherCalculators ? (
              <Button
                variant="outline"
                onClick={openOtherCalculators}
                className="mt-2"
                data-testid="open-other-calculators"
              >
                DESCHIDE MAI MULTE TIPURI DE CALCULATOR
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={backToMainCalculator}
                className="mt-2"
                data-testid="back-to-main-calculator"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Înapoi la Calculator Principal
              </Button>
            )}
          </motion.div>

          {/* Show progress only for other calculators */}
          {showOtherCalculators && (
            <div className="mb-6 sm:mb-8">
              <Progress value={progress} className="h-2" data-testid="calculator-progress" />
              <div className="flex justify-between mt-2 text-xs sm:text-sm text-slate-600">
                <span className={step >= 1 ? 'text-cyan-600 font-semibold' : ''}>Tip Acoperș</span>
                <span className={step >= 2 ? 'text-cyan-600 font-semibold' : ''}>Sub-tip</span>
                <span className={step >= 3 ? 'text-cyan-600 font-semibold' : ''}>Lucrări</span>
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            {/* MAIN CALCULATOR - Direct to work phases for Hidroizolații Bituminoase */}
            {!showOtherCalculators && mainCalculatorReady && (
              <motion.div
                key="main-calculator"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="glass-card p-4 sm:p-8"
                data-testid="main-calculator"
              >
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Selectează lucrările și materialele
                </h2>
                
                <div className="space-y-4 sm:space-y-6">
                  {phases.map(phase => (
                    <div key={phase.id} className="border border-cyan-100 rounded-xl p-4 sm:p-6 bg-white/50" data-testid={`phase-${phase.id}`}>
                      <div className="flex items-start gap-3 mb-3 sm:mb-4">
                        <Checkbox
                          id={phase.id}
                          checked={selectedPhases[phase.id] || false}
                          onCheckedChange={(checked) => handlePhaseToggle(phase.id, checked)}
                          data-testid={`phase-checkbox-${phase.id}`}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <Label htmlFor={phase.id} className="text-base sm:text-lg font-semibold text-slate-900 cursor-pointer block">
                            {phase.name}
                          </Label>
                          <p className="text-xs sm:text-sm text-slate-600 mt-1 line-clamp-2">{phase.description}</p>
                        </div>
                      </div>
                      
                      {selectedPhases[phase.id] && (
                        <div className="ml-0 sm:ml-8 mt-4 space-y-4">
                          <div>
                            <Label className="text-sm font-medium text-slate-700 mb-2 block">Selectează material</Label>
                            <Select
                              value={selectedMaterials[phase.id] || ''}
                              onValueChange={(value) => handleMaterialSelect(phase.id, value)}
                              data-testid={`material-select-${phase.id}`}
                            >
                              <SelectTrigger className="input-field w-full">
                                <SelectValue placeholder="Alege un material" />
                              </SelectTrigger>
                              <SelectContent className="max-w-[90vw]">
                                {materials
                                  .filter(m => !m.phase_id || m.phase_id === phase.id)
                                  .map(material => (
                                    <SelectItem key={material.id} value={material.id} className="text-xs sm:text-sm">
                                      <span className="block truncate">{material.name}</span>
                                      <span className="block text-xs text-slate-500">
                                        Mat: {material.price_per_unit.toFixed(2)} | Man: {material.labor_per_unit.toFixed(2)} LEI/{material.unit}
                                      </span>
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {selectedMaterials[phase.id] && (
                            <>
                              {materials.find(m => m.id === selectedMaterials[phase.id])?.has_thickness_options && (
                                <div>
                                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                                    Selectează grosimea (mm)
                                  </Label>
                                  <Select
                                    value={thicknessSelections[phase.id] || ''}
                                    onValueChange={(value) => setThicknessSelections(prev => ({ ...prev, [phase.id]: value }))}
                                  >
                                    <SelectTrigger className="input-field w-full">
                                      <SelectValue placeholder="Alege grosimea" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="50">50 mm</SelectItem>
                                      <SelectItem value="100">100 mm</SelectItem>
                                      <SelectItem value="150">150 mm</SelectItem>
                                      <SelectItem value="200">200 mm</SelectItem>
                                      <SelectItem value="300">300 mm</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                              
                              <div>
                                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                                  Cantitate ({materials.find(m => m.id === selectedMaterials[phase.id])?.unit || 'mp'})
                                </Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="Introduceți cantitatea"
                                  value={phaseQuantities[phase.id] || ''}
                                  onChange={(e) => handleQuantityChange(phase.id, e.target.value)}
                                  className="input-field"
                                />
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Shop Products Section */}
                <div className="mt-8 border-t border-cyan-200 pt-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Adaugă materiale din magazin
                    </h3>
                    <Button variant="outline" onClick={() => setShowShopModal(true)}>
                      <ShoppingBag className="w-4 h-4 mr-2" /> Deschide Magazin
                    </Button>
                  </div>
                  
                  {selectedShopProducts.length > 0 && (
                    <div className="space-y-3">
                      {selectedShopProducts.map(item => (
                        <div key={item.product.id} className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                          <div className="flex items-center gap-3">
                            <img src={item.product.image_url} alt={item.product.name} className="w-12 h-12 object-cover rounded" />
                            <div>
                              <p className="font-medium text-sm">{item.product.name}</p>
                              <p className="text-xs text-slate-500">{item.product.price.toFixed(2)} LEI/{item.product.unit}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateShopProductQuantity(item.product.id, parseInt(e.target.value) || 0)}
                              className="w-20 h-8 text-center"
                            />
                            <Button variant="ghost" size="sm" onClick={() => removeShopProduct(item.product.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Price Summary and Download Button */}
                <div className="mt-8 flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 flex-1">
                    <div className="glass-card p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 flex-1" data-testid="price-display">
                      <div className="flex justify-between sm:flex-col text-sm">
                        <span className="text-slate-600">Materiale:</span>
                        <span className="font-semibold text-slate-900">{totalMaterials.toFixed(2)} LEI</span>
                      </div>
                      <div className="flex justify-between sm:flex-col text-sm">
                        <span className="text-slate-600">Manoperă:</span>
                        <span className="font-semibold text-slate-900">{totalLabor.toFixed(2)} LEI</span>
                      </div>
                      <div className="flex justify-between sm:flex-col text-sm border-t sm:border-t-0 sm:border-l border-slate-200 pt-2 sm:pt-0 sm:pl-4">
                        <span className="text-slate-600">Subtotal:</span>
                        <span className="font-semibold text-slate-900">{totalPrice.toFixed(2)} LEI</span>
                      </div>
                      <div className="flex justify-between sm:flex-col text-sm">
                        <span className="text-slate-600">TVA (21%):</span>
                        <span className="font-semibold text-slate-900">{(totalPrice * 0.21).toFixed(2)} LEI</span>
                      </div>
                      <div className="flex justify-between sm:flex-col border-t sm:border-t-0 sm:border-l border-cyan-300 pt-2 sm:pt-0 sm:pl-4 bg-cyan-50/50 sm:bg-transparent p-2 sm:p-0 rounded">
                        <span className="text-sm text-cyan-700 font-semibold">TOTAL CU TVA:</span>
                        <span className="text-xl sm:text-2xl font-bold text-orange-500" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {(totalPrice * 1.21).toFixed(2)} LEI
                        </span>
                      </div>
                    </div>
                    <Button
                      onClick={openDownloadDialog}
                      className="btn-primary"
                      data-testid="download-pdf-btn"
                      disabled={totalPrice === 0}
                    >
                      <Download className="mr-2" /> Descarcă PDF
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* OTHER CALCULATORS - Step 1: Select roof type */}
            {showOtherCalculators && step === 1 && (
              <motion.div
                key="other-calc-step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="glass-card p-4 sm:p-8"
                data-testid="step-roof-type"
              >
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Selectează tipul de acoperiș
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                  {roofTypes.map(roofType => (
                    <div
                      key={roofType.id}
                      onClick={() => handleRoofTypeSelect(roofType)}
                      className="selection-card cursor-pointer"
                      data-testid={`roof-type-${roofType.id}`}
                    >
                      <img
                        src={roofType.image_url}
                        alt={roofType.name}
                        className="w-full h-24 sm:h-48 object-cover rounded-lg mb-2 sm:mb-4"
                      />
                      <h3 className="text-sm sm:text-xl font-bold text-slate-900 mb-1 sm:mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {roofType.name}
                      </h3>
                      <p className="text-slate-600 text-xs sm:text-sm line-clamp-2 hidden sm:block">{roofType.description}</p>
                    </div>
                  ))}
                </div>
                
                {/* Services Section */}
                {services.length > 0 && (
                  <div className="mt-8 sm:mt-12">
                    <h2 className="text-lg sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Sau selectează un serviciu complet
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                      {services.map(service => (
                        <div
                          key={service.id}
                          onClick={() => handleServiceSelect(service)}
                          className="selection-card cursor-pointer group"
                          data-testid={`service-${service.id}`}
                        >
                          <div className="relative">
                            <img
                              src={service.image_url}
                              alt={service.name}
                              className="w-full h-24 sm:h-40 object-cover rounded-lg mb-2 sm:mb-4 group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-orange-500 text-white px-1 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-bold">
                              {service.warranty}
                            </div>
                          </div>
                          <h3 className="text-xs sm:text-lg font-bold text-slate-900 mb-1 sm:mb-2 group-hover:text-cyan-600 transition line-clamp-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {service.name}
                          </h3>
                          <p className="text-slate-600 text-xs line-clamp-2 hidden sm:block">{service.short_description}</p>
                          <p className="text-cyan-600 text-[10px] sm:text-xs mt-1 sm:mt-2">{service.phases?.length || 0} etape</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* OTHER CALCULATORS - Step 2: Select sub-type */}
            {showOtherCalculators && step === 2 && selectedRoofType && (
              <motion.div
                key="other-calc-step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="glass-card p-4 sm:p-8"
                data-testid="step-sub-type"
              >
                <h2 className="text-lg sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Selectează sub-tipul pentru {selectedRoofType.name}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
                  {selectedRoofType.sub_types.map((subType, index) => (
                    <div
                      key={index}
                      onClick={() => handleSubTypeSelect(subType)}
                      className="selection-card cursor-pointer p-4 sm:p-6"
                      data-testid={`sub-type-${index}`}
                    >
                      <h3 className="text-sm sm:text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {subType}
                      </h3>
                    </div>
                  ))}
                </div>
                <div className="mt-4 sm:mt-6">
                  <Button onClick={() => setStep(1)} variant="outline" data-testid="back-to-step1">
                    <ChevronLeft className="mr-2" /> Înapoi
                  </Button>
                </div>
              </motion.div>
            )}

            {/* OTHER CALCULATORS - Step 3: Work phases */}
            {showOtherCalculators && step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="glass-card p-4 sm:p-8"
                data-testid="step-work-phases"
              >
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Selectează lucrările și materialele
                </h2>
                
                <div className="space-y-4 sm:space-y-6">
                  {phases.map(phase => (
                    <div key={phase.id} className="border border-cyan-100 rounded-xl p-4 sm:p-6 bg-white/50" data-testid={`phase-${phase.id}`}>
                      <div className="flex items-start gap-3 mb-3 sm:mb-4">
                        <Checkbox
                          id={phase.id}
                          checked={selectedPhases[phase.id] || false}
                          onCheckedChange={(checked) => handlePhaseToggle(phase.id, checked)}
                          data-testid={`phase-checkbox-${phase.id}`}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <Label htmlFor={phase.id} className="text-base sm:text-lg font-semibold text-slate-900 cursor-pointer block">
                            {phase.name}
                          </Label>
                          <p className="text-xs sm:text-sm text-slate-600 mt-1 line-clamp-2">{phase.description}</p>
                        </div>
                      </div>
                      
                      {selectedPhases[phase.id] && (
                        <div className="ml-0 sm:ml-8 mt-4 space-y-4">
                          <div>
                            <Label className="text-sm font-medium text-slate-700 mb-2 block">Selectează material</Label>
                            <Select
                              value={selectedMaterials[phase.id] || ''}
                              onValueChange={(value) => handleMaterialSelect(phase.id, value)}
                              data-testid={`material-select-${phase.id}`}
                            >
                              <SelectTrigger className="input-field w-full">
                                <SelectValue placeholder="Alege un material" />
                              </SelectTrigger>
                              <SelectContent className="max-w-[90vw]">
                                {materials
                                  .filter(m => !m.phase_id || m.phase_id === phase.id)
                                  .map(material => (
                                    <SelectItem key={material.id} value={material.id} className="text-xs sm:text-sm">
                                      <span className="block truncate">{material.name}</span>
                                      <span className="block text-xs text-slate-500">
                                        Mat: {material.price_per_unit.toFixed(2)} | Man: {material.labor_per_unit.toFixed(2)} LEI/{material.unit}
                                      </span>
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {selectedMaterials[phase.id] && (
                            <>
                              {/* Thickness selector for materials with thickness options */}
                              {materials.find(m => m.id === selectedMaterials[phase.id])?.has_thickness_options && (
                                <div>
                                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                                    Selectează grosimea (mm)
                                  </Label>
                                  <Select
                                    value={thicknessSelections[phase.id] || ''}
                                    onValueChange={(value) => setThicknessSelections(prev => ({ ...prev, [phase.id]: value }))}
                                    data-testid={`thickness-select-${phase.id}`}
                                  >
                                    <SelectTrigger className="input-field w-full">
                                      <SelectValue placeholder="Alege grosimea" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="50">50 mm</SelectItem>
                                      <SelectItem value="100">100 mm</SelectItem>
                                      <SelectItem value="150">150 mm</SelectItem>
                                      <SelectItem value="200">200 mm</SelectItem>
                                      <SelectItem value="300">300 mm</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                              
                              <div>
                                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                                  Cantitate ({materials.find(m => m.id === selectedMaterials[phase.id])?.unit || 'mp'})
                                </Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="Introduceți cantitatea"
                                  value={phaseQuantities[phase.id] || ''}
                                  onChange={(e) => handleQuantityChange(phase.id, e.target.value)}
                                  className="input-field"
                                  data-testid={`quantity-input-${phase.id}`}
                                />
                                {phaseQuantities[phase.id] && selectedMaterials[phase.id] && (
                                  <div className="mt-3 p-3 bg-cyan-50 rounded-lg">
                                    <div className="text-sm space-y-1">
                                      {(() => {
                                        const mat = materials.find(m => m.id === selectedMaterials[phase.id]);
                                        const qty = parseFloat(phaseQuantities[phase.id]) || 0;
                                        let matPrice = mat.price_per_unit;
                                        let labPrice = mat.labor_per_unit;
                                        
                                        // Apply thickness multiplier if applicable
                                        if (mat.has_thickness_options && thicknessSelections[phase.id]) {
                                          const thickness = parseInt(thicknessSelections[phase.id]);
                                          const multiplier = thickness / 50;
                                          matPrice = mat.price_per_unit * multiplier;
                                          labPrice = mat.labor_per_unit * multiplier;
                                        }
                                        
                                        return (
                                          <>
                                            {mat.has_thickness_options && thicknessSelections[phase.id] && (
                                              <div className="text-xs text-slate-600 mb-2">
                                                Grosime: {thicknessSelections[phase.id]} mm
                                              </div>
                                            )}
                                            <div className="flex justify-between">
                                              <span className="text-slate-600">Material:</span>
                                              <span className="font-semibold text-slate-900">{(matPrice * qty).toFixed(2)} LEI</span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span className="text-slate-600">Manoperă:</span>
                                              <span className="font-semibold text-slate-900">{(labPrice * qty).toFixed(2)} LEI</span>
                                            </div>
                                            <div className="flex justify-between pt-1 border-t border-cyan-200">
                                              <span className="font-semibold text-slate-700">Total fază:</span>
                                              <span className="font-bold text-orange-500">{((matPrice + labPrice) * qty).toFixed(2)} LEI</span>
                                            </div>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* ADAUGA MATERIALE DIN MAGAZIN Section */}
                <div className="mt-8 border-2 border-dashed border-orange-300 rounded-xl p-6 bg-orange-50/50" data-testid="shop-products-section">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <ShoppingBag className="w-6 h-6 text-orange-500" />
                      <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        ADAUGĂ MATERIALE DIN MAGAZIN
                      </h3>
                    </div>
                    <Button
                      onClick={() => setShowShopModal(true)}
                      className="btn-primary"
                      data-testid="open-shop-modal-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Adaugă Produs
                    </Button>
                  </div>
                  
                  <p className="text-sm text-slate-600 mb-4">
                    Adaugă produse suplimentare din magazin direct în deviz. Prețurile includ TVA.
                  </p>
                  
                  {selectedShopProducts.length > 0 ? (
                    <div className="space-y-3">
                      {selectedShopProducts.map(item => {
                        const priceWithoutTVA = item.product.price / 1.19;
                        return (
                          <div 
                            key={item.product.id} 
                            className="flex items-center justify-between bg-white rounded-lg p-4 shadow-sm"
                            data-testid={`shop-item-${item.product.id}`}
                          >
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <img 
                                src={item.product.image_url} 
                                alt={item.product.name}
                                className="w-12 h-12 object-cover rounded"
                              />
                              <div className="min-w-0">
                                <p className="font-medium text-slate-900 truncate">{item.product.name}</p>
                                <p className="text-sm text-slate-500">
                                  {priceWithoutTVA.toFixed(2)} LEI/{item.product.unit} (fără TVA)
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateShopProductQuantity(item.product.id, parseInt(e.target.value) || 0)}
                                className="w-20 text-center"
                                data-testid={`shop-qty-${item.product.id}`}
                              />
                              <span className="text-sm text-slate-600 w-16">{item.product.unit}</span>
                              <span className="font-bold text-orange-500 w-24 text-right">
                                {(priceWithoutTVA * item.quantity).toFixed(2)} LEI
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeShopProduct(item.product.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`remove-shop-${item.product.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      <div className="flex justify-end pt-3 border-t border-orange-200">
                        <div className="text-right">
                          <p className="text-sm text-slate-600">Total materiale magazin (fără TVA):</p>
                          <p className="text-xl font-bold text-orange-500">
                            {selectedShopProducts.reduce((sum, item) => sum + (item.product.price / 1.19) * item.quantity, 0).toFixed(2)} LEI
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-500">
                      <ShoppingBag className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Nu ai adăugat încă produse din magazin</p>
                    </div>
                  )}
                </div>

                <div className="mt-8 flex flex-col sm:flex-row justify-between gap-4">
                  <Button onClick={() => setStep(2)} variant="outline" data-testid="back-to-step2">
                    <ChevronLeft className="mr-2" /> Înapoi
                  </Button>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                    {/* Total Price Display - With TVA breakdown */}
                    <div className="glass-card p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-testid="price-display">
                      <div className="flex justify-between sm:flex-col text-sm">
                        <span className="text-slate-600">Materiale:</span>
                        <span className="font-semibold text-slate-900">{totalMaterials.toFixed(2)} LEI</span>
                      </div>
                      <div className="flex justify-between sm:flex-col text-sm">
                        <span className="text-slate-600">Manoperă:</span>
                        <span className="font-semibold text-slate-900">{totalLabor.toFixed(2)} LEI</span>
                      </div>
                      <div className="flex justify-between sm:flex-col text-sm border-t sm:border-t-0 sm:border-l border-slate-200 pt-2 sm:pt-0 sm:pl-4">
                        <span className="text-slate-600">Subtotal (fără TVA):</span>
                        <span className="font-semibold text-slate-900">{totalPrice.toFixed(2)} LEI</span>
                      </div>
                      <div className="flex justify-between sm:flex-col text-sm">
                        <span className="text-slate-600">TVA (21%):</span>
                        <span className="font-semibold text-slate-900">{(totalPrice * 0.21).toFixed(2)} LEI</span>
                      </div>
                      <div className="flex justify-between sm:flex-col border-t sm:border-t-0 sm:border-l border-cyan-300 pt-2 sm:pt-0 sm:pl-4 bg-cyan-50/50 sm:bg-transparent p-2 sm:p-0 rounded">
                        <span className="text-sm text-cyan-700 font-semibold">TOTAL CU TVA:</span>
                        <span className="text-xl sm:text-2xl font-bold text-orange-500" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {(totalPrice * 1.21).toFixed(2)} LEI
                        </span>
                      </div>
                    </div>
                    <Button
                      onClick={openDownloadDialog}
                      className="btn-primary"
                      data-testid="download-pdf-btn"
                      disabled={totalPrice === 0}
                    >
                      <Download className="mr-2" /> Descarcă PDF
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4 removed */}
          </AnimatePresence>
        </div>
      </div>

      {/* Shop Products Modal - Global for both calculators */}
      {showShopModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="shop-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden"
          >
            <div className="p-6 border-b bg-gradient-to-r from-orange-500 to-orange-600">
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Selectează Produse din Magazin
              </h3>
              <p className="text-orange-100 text-sm">Prețurile afișate includ TVA (19%)</p>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {shopProducts.map(product => (
                  <div 
                    key={product.id} 
                    className="border rounded-lg p-4 hover:border-orange-300 hover:bg-orange-50/50 transition cursor-pointer"
                    onClick={() => addShopProduct(product)}
                    data-testid={`modal-product-${product.id}`}
                  >
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-full h-32 object-cover rounded mb-3"
                    />
                    <h4 className="font-medium text-slate-900 text-sm line-clamp-2 mb-2">{product.name}</h4>
                    <p className="text-xs text-slate-500 mb-2">{product.category}</p>
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-orange-500">{product.price.toFixed(2)} LEI</span>
                      <span className="text-xs text-slate-500">/{product.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 border-t bg-slate-50 flex justify-end">
              <Button
                variant="outline"
                onClick={() => setShowShopModal(false)}
                data-testid="close-shop-modal"
              >
                Închide
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />
      
      {/* Download Dialog - Client Info */}
      <Dialog open={showDownloadDialog} onOpenChange={setShowDownloadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-600" />
              Generează Deviz PDF
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <div>
                <Label className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4 text-slate-500" />
                  Nume Client *
                </Label>
                <Input
                  value={clientInfo.clientName}
                  onChange={(e) => setClientInfo({...clientInfo, clientName: e.target.value})}
                  placeholder="ex: Ion Popescu / S.C. Firma S.R.L."
                  data-testid="client-name-input"
                />
              </div>
              
              <div>
                <Label className="flex items-center gap-2 mb-1">
                  <Phone className="w-4 h-4 text-slate-500" />
                  Telefon Client
                </Label>
                <Input
                  value={clientInfo.clientPhone}
                  onChange={(e) => setClientInfo({...clientInfo, clientPhone: e.target.value})}
                  placeholder="ex: 0722 123 456"
                  data-testid="client-phone-input"
                />
              </div>
              
              <div>
                <Label className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-slate-500" />
                  Nume Proiect
                </Label>
                <Input
                  value={clientInfo.projectName}
                  onChange={(e) => setClientInfo({...clientInfo, projectName: e.target.value})}
                  placeholder="ex: Hidroizolație Vilă Iași"
                  data-testid="project-name-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDownloadDialog(false)}>
              Anulează
            </Button>
            <Button 
              onClick={handleDownloadPDF}
              className="btn-primary"
              disabled={!clientInfo.clientName.trim()}
            >
              <Download className="w-4 h-4 mr-2" />
              Descarcă PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Calculator;
